// eslint-disable-next-line no-unused-vars
import React from 'react';
import { node, bool, oneOfType, number, string, object, func, array } from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Button, SkeletonText, Loading } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Truck, Info } from 'lucide-react';

import { NWNotification, NWOrderTypeBadge } from 'Components/shared';

import usePermissions from 'Hooks/usePermissions';
import { PERMISSION } from 'Constants';

const StyledOrderPage = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 20px;

  .cds--data-table-container {
    width: 100%;
  }

  .orderSummaryContainer {
    display: flex;
    justify-content: flex-end;
  }

  .orderSummary {
    margin: 10px 0 30px;
    background-color: transparent;
    border: 0;
    width: 100%;
    max-width: 590px;

    .title {
      display: none;
    }

    .item {
      text-align: right;
      font-size: 1.125rem;
      line-height: 1.25rem;
      align-items: center !important;
      margin: 8px 0;
      grid-template-columns: auto 180px;

      p {
        text-align: right;
        margin-right: 10px;
      }

      &.total {
        display: grid !important;
        margin-top: 10px;
        grid-template-columns: auto 180px;

        span {
          margin: 0 !important;
        }
      }
    }

    hr {
      display: none;
    }
  }

  .header,
  .footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 15px 0;
    width: 100%;

    .back {
      display: block;
      padding: 0;
      width: 200px;
      height: 40px;
      border: 1px solid ${ props => props.theme.colors.blue };
      text-align: center;
      font-size: 0.875rem;
      line-height: 40px;
      color: ${ props => props.theme.colors.blue };
    }
  }

  .header {
    flex-direction: column;
    align-items: flex-start;
    margin: 0;

    .titleContainer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 5px;
      color: ${ props => props.theme.colors.darkGray };
      width: 100%;

      .title {
        display: flex;
        align-items: center;
      }

      svg.dsIcon {
        margin-right: 8px;
        width: 27px;
      }

      h1 {
        font-size: 1.875rem;
        margin: 15px 18px 10px 0;
        display: flex;
        align-items: center;
        white-space: nowrap;

        .cds--skeleton__text {
          min-width: 70px;
          max-width: 70px;
        }

        span {
          font-weight: 600;
          margin-right: 5px;
        }
      }

      .stockReservationBadge {
        display: flex;
        align-items: center;
        gap: 10px;
        
        span {
          &.rsvp {
            color: #E8720B;
            font-size: 1rem;
            display: flex;
            align-items: center;
            &:before {
              position: relative;
              top: 2px;
              left: -10px;
              content: ${ () => `url(${Info})` };
            }
          }
          padding: 0 19px;
          margin-top: 5px;
          line-height: 22px;
        }
      }
    }

    .messages {
      margin: 10px 0 0;
    }
  }

  .submit {
    width: 100%;
    min-width: 250px;
    max-width: 280px;
    height: 40px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    border-color: inherit;
    background-color: ${ props => props.theme.colors.default };

    &:not(:disabled) {
      color: ${ props => props.theme.colors.white };
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    .header {
      .back {
        width: 100%;
      }

      .titleContainer {
        flex-direction: column;

        .title {
          flex-direction: column;

          .stockReservationBadge {
            flex-direction: column;
          }
        }
      }
    }

    .orderSummaryContainer {
      width: 100%;
    }

    .footer {
      flex-direction: column;
      
      .back {
        width: 100%;
      }
    }

    .submit {
      width: 100%;
      max-width: 100%;
      margin-top: 20px;
    }
  }
`;

const OrderPage = (
  {
    children,
    formRef,
    onSubmit,
    downloading,
    onGenerateInvoice,
    loadingInvoice,
    loading,
    errors,
    stockReservation,
    number: orderNum,
    ds,
    canPlaceOrder,
    isSupplierOrder,
  }
) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isExternalReseller, can } = usePermissions();
  const canAddToCart = can(PERMISSION.ADD_TO_CART);
  const orderNumber = orderNum?.toString();
  const body = (
    <>
      <div className="header">
        <Link
          to="/orders"
          className="back"
        >
          { '<' } { t('common.backTo', { label: t('orders.orders') }) }
        </Link>
        <div className="titleContainer">
          <div className="title">
            <h1>
              { ds && <Truck className="dsIcon" /> }
              <span>{ t('order.orderId', { orderId: '' }) }</span>{ orderNumber || <SkeletonText /> }
            </h1>
            { !stockReservation && ds && (
              <NWOrderTypeBadge>
                { t('order.dropShipmentOrderType') }
              </NWOrderTypeBadge>
            ) }
            { stockReservation && !ds && (
              <div className="stockReservationBadge">
                <NWOrderTypeBadge>
                  { t('order.stockReservation') }
                </NWOrderTypeBadge>
                <span className="rsvp">
                  { t('order.reservationWarning') }
                </span>
              </div>
            ) }
            { !stockReservation && !ds && orderNumber && (
              <NWOrderTypeBadge>
                { isSupplierOrder ? t('products.supplierStock') : t('products.nodStock') }
              </NWOrderTypeBadge>
            ) }
          </div>
          { (orderNumber && orderNumber !== '0' && !stockReservation && !isExternalReseller) && (
            <Button
              onClick={ onGenerateInvoice }
              data-test="onGenerateInvoice"
              data-testid={ orderNumber }
              className="submit"
              kind="danger"
              disabled={ loadingInvoice }
              type="submit"
            >
              { downloading && <Loading small withOverlay={ false } /> }
              { t('order.generateProformInvoice') }
            </Button>
          ) }
        </div>
        { !!errors.length && <NWNotification errors={ errors } /> }
      </div>
      { children }
      <div className="footer">
        <Link
          to="/orders"
          className="back"
        >
          { '<' } { t('common.backTo', { label: t('orders.orders') }) }
        </Link>
        { stockReservation && (
          <Button
            onClick={ onSubmit }
            data-test="onSubmitReservation"
            className="submit"
            kind="danger"
            disabled={ loading || !canPlaceOrder || !canAddToCart }
            type="submit"
          >
            { loading && <Loading active={ loading } small withOverlay={ false } /> }
            { t('order.placeOrder') }
          </Button>
        ) }
      </div>
    </>
  );
  return (
    <StyledOrderPage theme={ theme }>
      { stockReservation
        ? <form ref={ formRef }>{ body }</form>
        : body
      }
    </StyledOrderPage>
  );
};

OrderPage.propTypes = {
  children: node,
  formRef: object,
  ds: bool,
  number: oneOfType([number, string]),
  loading: bool,
  loadingInvoice: bool,
  downloading: bool,
  stockReservation: bool,
  canPlaceOrder: bool,
  onGenerateInvoice: func,
  onSubmit: func,
  errors: array,
  isSupplierOrder: bool,
};
OrderPage.displayName = 'OrderPageComponent';
export default OrderPage;
